import { chiffrer, creerCle, dechiffrer, type Chiffre } from '../../../horsLigne/chiffrement.js';
import {
  creerMagasinPresentation,
  prefixePortee,
  type MagasinPresentation,
  type PresentationPreparee,
} from './magasin.js';
import type { PorteeFile } from '../../stand/fileHorsLigne.js';

interface EnregistrementChiffre extends PorteeFile {
  readonly chiffre: Chiffre;
}
export interface StockageChiffrePresentation {
  obtenirCle(portee: PorteeFile, proposition?: CryptoKey): Promise<CryptoKey | null>;
  ecrire(id: string, valeur: EnregistrementChiffre): Promise<void>;
  lire(id: string): Promise<EnregistrementChiffre | null>;
  identifiants(): Promise<string[]>;
  effacer(id: string): Promise<void>;
  effacerPortee(portee: PorteeFile): Promise<void>;
  toutEffacer(): Promise<void>;
}
interface ImageSerialisee {
  url: string;
  type: string;
  octets: number[];
}
type PreparationSerialisee = Omit<PresentationPreparee, 'images'> & { images: ImageSerialisee[] };

export function magasinPresentationChiffre(
  stockage: StockageChiffrePresentation,
  moteur: Crypto = crypto,
  maintenant = () => new Date(),
): MagasinPresentation {
  const magasin = creerMagasinPresentation(
    {
      async ecrire(id, preparation) {
        const images: ImageSerialisee[] = [];
        for (const [url, image] of preparation.images)
          images.push({
            url,
            type: image.type,
            octets: [...new Uint8Array(await image.arrayBuffer())],
          });
        const existante = await stockage.obtenirCle(preparation);
        const cle = existante ?? (await stockage.obtenirCle(preparation, await creerCle(moteur)));
        if (!cle) throw new Error('La clé de présentation est indisponible.');
        try {
          const chiffre = await chiffrer(moteur, cle, id, { ...preparation, images });
          await stockage.ecrire(id, {
            compteId: preparation.compteId,
            galerieSlug: preparation.galerieSlug,
            chiffre,
          });
        } catch (erreur) {
          await stockage.effacer(id);
          throw erreur;
        }
      },
      async lire(id) {
        const valeur = await stockage.lire(id);
        if (!valeur) return null;
        const cle = await stockage.obtenirCle(valeur);
        if (!cle) {
          await stockage.effacer(id);
          return null;
        }
        try {
          const preparation = (await dechiffrer(
            moteur,
            cle,
            id,
            valeur.chiffre,
          )) as PreparationSerialisee;
          return {
            ...preparation,
            images: new Map(
              preparation.images.map((image) => [
                image.url,
                new Blob([new Uint8Array(image.octets)], { type: image.type }),
              ]),
            ),
          };
        } catch {
          await stockage.effacer(id);
          return null;
        }
      },
      identifiants: () => stockage.identifiants(),
      effacer: (id) => stockage.effacer(id),
    },
    maintenant,
  );
  return {
    ...magasin,
    effacerPortee: (portee) => stockage.effacerPortee(portee),
    toutEffacer: () => stockage.toutEffacer(),
  };
}

function resultat<T>(requete: IDBRequest<T>): Promise<T> {
  return new Promise((resoudre, rejeter) => {
    requete.onsuccess = () => resoudre(requete.result);
    requete.onerror = () => rejeter(requete.error);
  });
}
export function magasinPresentationIndexedDb(
  fabrique: IDBFactory = indexedDB,
): MagasinPresentation {
  async function transaction<T>(
    mode: IDBTransactionMode,
    faire: (tx: IDBTransaction) => Promise<T>,
  ): Promise<T> {
    const ouverture = fabrique.open('artosera-presentation', 2);
    ouverture.onupgradeneeded = () => {
      const base = ouverture.result;
      if (!base.objectStoreNames.contains('selections')) base.createObjectStore('selections');
      if (!base.objectStoreNames.contains('cles')) base.createObjectStore('cles');
    };
    const base = await resultat(ouverture);
    base.onversionchange = () => base.close();
    try {
      const tx = base.transaction(['selections', 'cles'], mode);
      const fin = new Promise<void>((resoudre, rejeter) => {
        tx.oncomplete = () => resoudre();
        tx.onabort = tx.onerror = () => rejeter(tx.error);
      });
      const [valeur] = await Promise.all([faire(tx), fin]);
      return valeur;
    } finally {
      base.close();
    }
  }
  return magasinPresentationChiffre({
    obtenirCle: (portee, proposition) =>
      transaction(proposition ? 'readwrite' : 'readonly', async (tx) => {
        const magasin = tx.objectStore('cles');
        const id = prefixePortee(portee);
        const cle = ((await resultat(magasin.get(id))) as CryptoKey | undefined) ?? proposition;
        if (cle && proposition) await resultat(magasin.put(cle, id));
        return cle ?? null;
      }),
    ecrire: async (id, valeur) => {
      await transaction('readwrite', async (tx) => {
        await resultat(tx.objectStore('selections').put(valeur, id));
      });
    },
    lire: (id) =>
      transaction(
        'readonly',
        async (tx) =>
          ((await resultat(tx.objectStore('selections').get(id))) as
            EnregistrementChiffre | undefined) ?? null,
      ),
    identifiants: () =>
      transaction('readonly', async (tx) =>
        (await resultat(tx.objectStore('selections').getAllKeys())).map(String),
      ),
    effacer: async (id) => {
      await transaction('readwrite', async (tx) => {
        const selections = tx.objectStore('selections');
        const prefixe = id.slice(0, id.lastIndexOf(':') + 1);
        await resultat(selections.delete(id));
        const restantes = await resultat(selections.getAllKeys());
        // La dernière sélection emporte sa clé dans la même transaction.
        if (!restantes.some((cle) => String(cle).startsWith(prefixe)))
          await resultat(tx.objectStore('cles').delete(prefixe));
      });
    },
    effacerPortee: async (portee) => {
      await transaction('readwrite', async (tx) => {
        const prefixe = prefixePortee(portee);
        await resultat(tx.objectStore('cles').delete(prefixe));
        const selections = tx.objectStore('selections');
        for (const id of await resultat(selections.getAllKeys()))
          if (String(id).startsWith(prefixe)) await resultat(selections.delete(id));
      });
    },
    toutEffacer: async () => {
      await transaction('readwrite', async (tx) => {
        await resultat(tx.objectStore('cles').clear());
        await resultat(tx.objectStore('selections').clear());
      });
    },
  });
}
